/**
 * Keyboard behaviour for native checkboxes and radios, bound on `SCACheckboxBase` / `SCARadioButtonBase` —
 * both take the `<input>` from a slot, so the app has ~65 of them and only these two roots in common.
 *
 * Enter picks (the platform reserves it for form submit, and nothing here is a `<form>`). For radios, Tab and
 * the arrow keys move focus between the options *without* selecting — natively a group is one tab stop and
 * arrows select as they move, which rewrote the value just from browsing.
 */

const ARROW_STEP: Record<string, 1 | -1 | undefined> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

function focusedCheckable(event: KeyboardEvent, type: "checkbox" | "radio"): HTMLInputElement | null {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return null;
  if (target.type !== type) return null;
  if (target.disabled) return null;
  // `tabindex="-1"` means the author put this control outside keyboard navigation — a list row whose widget
  // drives Enter itself. It can still hold focus after a click.
  if (target.tabIndex < 0) return null;
  return target;
}

function activate(event: KeyboardEvent, input: HTMLInputElement): void {
  event.preventDefault();
  // Runs the platform's own activation, so `change`, `:checked` and any `v-model` follow as for a mouse click.
  // The enclosing `<label>` does not re-forward it: that is a no-op for events targeting its own control.
  input.click();
}

/** The group `input` belongs to, in document order — same-name radios, scoped as the platform scopes them. */
function radioGroup(input: HTMLInputElement): HTMLInputElement[] {
  if (!input.name) return [];
  const scope: ParentNode = input.form ?? (input.getRootNode() as ParentNode);
  return Array.from(
    scope.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(input.name)}"]:not([disabled])`),
  );
}

/** Whether focus moved. A member the platform refuses focus to (inside a collapsed branch) is skipped. */
function moveWithinGroup(from: HTMLInputElement, step: 1 | -1): boolean {
  const group = radioGroup(from);
  const current = group.indexOf(from);
  if (current < 0) return false;

  for (let i = current + step; i >= 0 && i < group.length; i += step) {
    group[i].focus();
    if (document.activeElement === group[i]) return true;
  }
  return false;
}

/** Bind to the element wrapping a checkbox `<input>`. */
export function onCheckboxKeyDown(event: KeyboardEvent): void {
  if (event.key !== "Enter") return;
  const input = focusedCheckable(event, "checkbox");
  if (input) activate(event, input);
}

/** Bind to the element wrapping a radio `<input>`. */
export function onRadioKeyDown(event: KeyboardEvent): void {
  const input = focusedCheckable(event, "radio");
  if (!input) return;

  if (event.key === "Enter") {
    activate(event, input);
    return;
  }

  const arrow = ARROW_STEP[event.key];
  if (arrow) {
    // Always prevented, including at the ends: the native fallback there is to wrap around and select.
    event.preventDefault();
    moveWithinGroup(input, arrow);
    return;
  }

  // Tab is only claimed when it moved; at either end it means "leave the group" and travels on.
  if (event.key === "Tab" && moveWithinGroup(input, event.shiftKey ? -1 : 1)) event.preventDefault();
}
